import {
  Button,
  Callout,
  Field,
  Input,
  Pill,
  Radio,
  RadioGroup,
  Segmented,
  SegmentedOption,
  Select,
} from '@wtfalch/design';
import {
  findPrincipals,
  type MailboxNode,
  type MailboxRight,
  type MailboxRights,
  type MailClient,
  mailboxes,
  type PrincipalMatch,
  shareMailbox,
  unshareMailbox,
  walk,
} from '@wtfalch/email/mailbox';
import { useRef, useState } from 'react';

import { folderName } from './format.ts';

type Level = 'read' | 'change';

/** Reading, and marking read: enough to look at a folder. */
const READ: Partial<Record<MailboxRight, boolean>> = { mayReadItems: true };
/** Everything a colleague needs to work in the folder, short of sharing it
 *  on, renaming or deleting it. */
const CHANGE: Partial<Record<MailboxRight, boolean>> = {
  mayReadItems: true,
  mayAddItems: true,
  mayRemoveItems: true,
  maySetSeen: true,
  maySetKeywords: true,
};

export const RIGHTS_FOR: Record<Level, Partial<Record<MailboxRight, boolean>>> = {
  read: READ,
  change: CHANGE,
};

const levelOf = (rights: MailboxRights): string =>
  rights.mayAddItems || rights.mayRemoveItems ? 'Read and change' : 'Read';

/**
 * Sharing a folder with a colleague (`Mail-sharing` board): pick a folder,
 * find the colleague by name or address, choose what they may do, and share.
 * Below, who the folder is shared with, each with a Remove.
 */
export function Sharing({
  client,
  tree,
  onTreeChange,
}: {
  client: MailClient;
  tree: readonly MailboxNode[] | null;
  onTreeChange: (tree: readonly MailboxNode[]) => void;
}) {
  const folders = tree ? [...walk(tree)].filter((m) => m.myRights.mayShare) : [];
  const [folderId, setFolderId] = useState('');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<readonly PrincipalMatch[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [level, setLevel] = useState<Level>('read');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const names = useRef(new Map<string, string>());

  const folder = folders.find((m) => m.id === folderId) ?? folders[0] ?? null;
  const person = matches?.find((m) => m.principalId === chosen) ?? null;

  const refresh = async () => onTreeChange(await mailboxes(client));

  const find = async () => {
    const q = query.trim();
    setError('');
    setSaved('');
    if (!q) {
      setError('Type a name or an address.');
      return;
    }
    setBusy(true);
    try {
      const found = await findPrincipals(client, q);
      for (const m of found) names.current.set(m.accountId, m.name || m.email);
      setMatches(found);
      setChosen(found.length === 1 ? (found[0]?.principalId ?? '') : '');
    } catch (err) {
      setMatches(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!folder || !person) return;
    setError('');
    setSaved('');
    setBusy(true);
    try {
      if (person.accountId === (await client.accountId())) {
        setError('That is your own account.');
        return;
      }
      await shareMailbox(client, folder.id, person.accountId, RIGHTS_FOR[level]);
      await refresh();
      setSaved(`${folderName(folder)} is shared with ${person.name || person.email}.`);
      setMatches(null);
      setChosen('');
      setQuery('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (accountId: string) => {
    if (!folder) return;
    setError('');
    setSaved('');
    setBusy(true);
    try {
      await unshareMailbox(client, folder.id, accountId);
      await refresh();
      setSaved(
        `${folderName(folder)} is no longer shared with ${names.current.get(accountId) ?? accountId}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const shared = folder ? Object.entries(folder.shareWith) : [];

  return (
    <div className="flex flex-col gap-4 mail-set-form">
      <p className="text-sm text-muted m-0">
        Give a colleague access to one folder. They see it in their own folder list.
      </p>
      <Field label="Folder">
        <Select
          value={folder?.id ?? ''}
          onChange={(e) => setFolderId(e.target.value)}
          aria-label="Folder to share"
        >
          {folders.map((m) => (
            <option key={m.id} value={m.id}>
              {`${'  '.repeat(m.path.length - 1)}${folderName(m)}`}
            </option>
          ))}
        </Select>
      </Field>
      <form
        className="flex items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void find();
        }}
      >
        <Field label="Colleague" hint="A name or an email address.">
          <Input
            block
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setMatches(null);
              setChosen('');
            }}
          />
        </Field>
        <Button type="submit" disabled={busy}>
          Find
        </Button>
      </form>
      {matches?.length === 0 && (
        <p className="text-sm text-muted m-0" role="status">
          {`Nobody at your company matches "${query.trim()}".`}
        </p>
      )}
      {matches && matches.length > 1 && (
        <RadioGroup label="Which one?" value={chosen} onChange={setChosen}>
          {matches.map((m) => (
            <Radio key={m.principalId} value={m.principalId}>
              {`${m.name} (${m.email})`}
            </Radio>
          ))}
        </RadioGroup>
      )}
      {person && (
        <>
          <p className="text-sm m-0">{`${person.name} (${person.email})`}</p>
          <Segmented
            label="They may"
            size="md"
            value={level}
            onChange={(v) => setLevel(v as Level)}
          >
            <SegmentedOption value="read">Read</SegmentedOption>
            <SegmentedOption value="change">Read and change</SegmentedOption>
          </Segmented>
          <div>
            <Button kind="primary" disabled={busy || !folder} onPress={() => void share()}>
              Share
            </Button>
          </div>
        </>
      )}
      <div className="mail-set-status" role="status">
        {error ? (
          <Callout tone="bad">{error}</Callout>
        ) : saved ? (
          <Callout tone="good">{saved}</Callout>
        ) : null}
      </div>
      <h3 className="h3 m-0">Shared with</h3>
      {shared.length === 0 ? (
        <p className="text-sm text-muted m-0">Nobody yet.</p>
      ) : (
        <ul className="mail-share-list">
          {shared.map(([accountId, rights]) => (
            <li key={accountId} className="flex items-center gap-3">
              <span className="grow min-w-0 truncate">
                {names.current.get(accountId) ?? accountId}
              </span>
              <Pill quiet>{levelOf(rights)}</Pill>
              <Button size="sm" disabled={busy} onPress={() => void remove(accountId)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
